import { api, tokens } from '../../lib/api';
import type { MoveReason, StockChannel, Unit } from '@artmozaik/shared';

/** Ürün ekranlarının (stok listesi, ürün kartı, yeni ürün) ortak türleri ve yardımcıları. */

export type DosyaTuru = 'GORSEL' | 'TEKNIK' | 'BELGE';
export type Dosya = { id: number; kind: DosyaTuru; filename: string; mime: string; size: number; note: string | null; sort?: number; url: string };

export type Detay = {
  id: number; code: string; name: string; unit: Unit; notes: string | null;
  threshold: number; official: number; offrec: number | null;
  listPriceKurus: number | null; maxDiscountPct: number | null;
  category: string; boxM2: number | null; vatPct: number; imageUrl: string | null; active: boolean;
  description: string | null; brand: string | null; specs: Record<string, string>; dosyalar: Dosya[];
  siteName: string | null;
  komsu?: { onceki: number | null; sonraki: number | null };
  /** null = rol fasonu göremez. */
  fasonda?: { toplam: number; isler: { isId: number; no: string; fasoncu: string; kalan: number; gun: number }[] } | null;
  /** Yalnız maliyeti görebilen rolde gelir. */
  purchaseDiscountPct?: number | null; costKurus?: number | null;
  maliyet?: { alisIskontoPct: number | null; maliyetKurus: number | null; maliyetKaynak: string | null };
  aliases: { id: number; supplierCode: string; supplier: string | null; nameSnapshot: string | null }[];
  /** null = rol maliyeti göremez; bölüm hiç çizilmez. */
  priceHistory: { id: number; date: string; unitPriceKurus: number; supplier: string | null; invoiceNo: string | null }[] | null;
  hareketler: { id: number; at: string; channel: StockChannel; qty: number; reason: MoveReason; note: string | null; by: string }[];
};

export type Gecmis = {
  /** null = rol teklifleri göremez. */
  teklifler: { id: number; no: string; durum: string; tarih: string; cari: string | null; miktar: number; iskontoPct: number;
    siparisler: { id: number; no: string }[] }[] | null;
  /** null = rol satış tutarlarını göremez. */
  satis: { bagli: boolean; gun30: { miktar: number; tutarKurus: number }; gun90: { miktar: number; tutarKurus: number };
    faturalar: { id: number; no: string; tarih: string; cari: string; iade: boolean; miktar: number; tutarKurus: number }[] } | null;
};

export const KATEGORI_AD: Record<string, string> = {
  URETIM: 'Üretim', ALSAT: 'Al-sat', HAMMADDE: 'Hammadde', HIZMET: 'Hizmet',
};
export const KATEGORILER = ['URETIM', 'ALSAT', 'HAMMADDE', 'HIZMET'] as const;
export const BIRIMLER: Unit[] = ['KUTU', 'M2', 'ADET', 'KG', 'LT', 'MTUL'];

/** Teknik özelliklerin bilinen alanları (anahtar → etiket). Kayıtta anahtar, ekranda etiket görünür. */
export const OZELLIKLER: [string, string][] = [
  ['ebat', 'Ebat'], ['kalinlik', 'Kalınlık'], ['yuzey', 'Yüzey'], ['renk', 'Renk'],
  ['seri', 'Seri / koleksiyon'], ['malzeme', 'Malzeme'],
  ['kullanim', 'Kullanım alanı (duvar/zemin/havuz…)'], ['kaymazlik', 'Kaymazlık sınıfı (R9–R13)'],
  ['don', 'Dona dayanım'], ['agirlik', 'Kutu ağırlığı (kg)'], ['kutuAdet', 'Kutu içi adet'],
  ['paletKutu', 'Palet başına kutu'], ['mensei', 'Menşei'], ['barkod', 'Barkod'], ['gtip', 'GTİP'],
];
export const OZELLIK_ETIKET = new Map(OZELLIKLER);
const tr = (s: string) => s.trim().toLocaleLowerCase('tr');
const ETIKETTEN_ANAHTAR = new Map(OZELLIKLER.map(([k, e]) => [tr(e), k]));
/** Ekranda yazılan özellik adını kayıt anahtarına çevirir (bilinen etiketse anahtarı, değilse kendisi). */
export const ozellikAnahtari = (ad: string) => ETIKETTEN_ANAHTAR.get(tr(ad)) ?? ad.trim();

export const GORSEL_KABUL = 'image/jpeg,image/png,image/webp,image/gif';
export const BELGE_KABUL = '.pdf,.png,.jpg,.jpeg,.webp,.dwg,.dxf,.xlsx,.docx';

export const boyut = (n: number) =>
  n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} MB`;
export const uzanti = (ad: string) => (ad.includes('.') ? ad.split('.').pop()!.toUpperCase() : '—');
/** Galeri görselinin adresi sunucuda mutlak üretilir; sayfada aynı kökten açılsın diye yol kısmı alınır. */
export const yerel = (url: string) => { const i = url.indexOf('/api/urun-gorsel/'); return i >= 0 ? url.slice(i) : url; };
/** Miktar: en çok 3 ondalık, Türkçe biçim. */
export const miktar = (n: number) => n.toLocaleString('tr-TR', { maximumFractionDigits: 3 });
/** Sayı → form alanı metni (virgüllü). */
export const alanMetni = (n: number | null | undefined) => (n == null ? '' : String(n).replace('.', ','));
/** Form alanı metni → sayı. Boş = null; okunamayan = NaN. */
export const sayiOku = (v: string): number | null => {
  const t = v.trim();
  if (t === '') return null;
  // "1.690,50" ve "1690.50" ikisi de kabul: virgül varsa nokta binliktir.
  const duz = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  return /^-?\d+(\.\d+)?$/.test(duz) ? Number(duz) : NaN;
};

/**
 * Jetonla ham istek (dosya yükleme / indirme). `api()` yalnız JSON taşıdığı
 * için burada ayrı; jetonun süresi dolduysa bir kez yenileyip tekrar dener.
 */
export async function yetkiliFetch(yol: string, init: RequestInit = {}): Promise<Response> {
  const cagir = () => fetch(yol, { ...init, headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {} });
  let res = await cagir();
  if (res.status === 401 && tokens.refresh) {
    await api('/api/me').catch(() => undefined);
    res = await cagir();
  }
  return res;
}

/** Sunucudan gelen dosyayı (Excel vb.) tarayıcıya indirtir. */
export async function indir(yol: string, ad: string): Promise<void> {
  const res = await yetkiliFetch(yol);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? 'Dosya oluşturulamadı');
  kaydet(await res.blob(), ad);
}
export function kaydet(blob: Blob, ad: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = ad; a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/* ─── Liste ↔ kart arası taşınan küçük durum (oturum boyunca) ───────────── */
const SIRA = 'am_stok_sira';
const DONUS = 'am_stok_donus';
/** Listenin o anki sırası: ürün kartındaki önceki / sonraki okları bunu izler. */
export const siraYaz = (ids: number[]) => { try { sessionStorage.setItem(SIRA, JSON.stringify(ids.slice(0, 2000))); } catch { /* dolu */ } };
export const siraOku = (): number[] => { try { const v = JSON.parse(sessionStorage.getItem(SIRA) ?? '[]'); return Array.isArray(v) ? v : []; } catch { return []; } };
/** Listenin süzgeç + sayfa adresi: karttan geri dönünce aynı yere gelinir. */
export const donusYaz = (sorgu: string) => { try { sessionStorage.setItem(DONUS, sorgu); } catch { /* dolu */ } };
export const donusOku = (): string => { try { return sessionStorage.getItem(DONUS) ?? ''; } catch { return ''; } };
